import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import {
  useGetPostsByThreadQuery,
  useCreatePostMutation,
  useUpdateLastSeenPostMutation,
} from "../../../graphql/generated/graphql";
import DiscussionHeader from "../../../components/customCoursework/StudentDiscussions/DiscussionHeader/DiscussionHeader";
import ResponseCardList from "../../../components/customCoursework/StudentDiscussions/ResponseCardList/ResponseCardList";
import { useAuth } from "../../../hooks/useAuth";

interface Author {
  id: string;
  name: string;
}

interface Reply {
  id: string;
  message: string;
  depth: number;
  created: string;
  author: Author;
}

interface Post {
  id: string;
  message: string;
  depth: number;
  threadId: string;
  created: string;
  lastModified: string;
  author: Author;
  replies: Reply[];
}

interface StudentDiscussionContainerProps {
  threadId: string;
  threadTitle: string;
  page?: number;
  pageSize?: number;
}

const StudentDiscussionContainer: React.FC<StudentDiscussionContainerProps> = ({
  threadId,
  threadTitle,
  page = 1,
  pageSize = 20,
}) => {
  const [createPostMutation] = useCreatePostMutation();
  const [updateLastSeenPostMutation] = useUpdateLastSeenPostMutation();

  // Keep track of posts that have been viewed in this session
  const [viewedPostIds, setViewedPostIds] = useState<Set<string>>(new Set());
  // Keep track of the most recent post ID viewed
  const [mostRecentViewedPostId, setMostRecentViewedPostId] = useState<string | null>(null);

  const { meData } = useAuth();
  const currentUser = meData?.me
    ? {
        id: meData.me.id,
        name: meData.me.name || "Unknown User",
      }
    : null;

  const {
    data: postsData,
    loading,
    error,
    refetch,
  } = useGetPostsByThreadQuery({
    variables: {
      threadId,
      page,
      pageSize,
    },
    skip: !threadId,
  });

  const allPosts = postsData?.getPostsByThread?.data || [];

  // Separate initial post (depth 0) from responses (depth 1+)
  const initialPost = allPosts.find((post) => post.depth === 0);
  const responsePosts = allPosts.filter((post) => post.depth === 1);

  // Handle when a post is viewed (called by ResponseCard)
  const handlePostViewed = useCallback(
    (postId: string) => {
      setViewedPostIds((prev) => new Set([...prev, postId]));

      // Update the most recent viewed post (we'll use this when leaving the thread)
      const post = allPosts.find((p) => p.id === postId);
      if (post) {
        setMostRecentViewedPostId(postId);
      }
    },
    [allPosts]
  );

  // Mark posts as seen when leaving the thread (cleanup effect)
  useEffect(() => {
    return () => {
      // When component unmounts (leaving the thread), mark the most recent viewed post as seen
      if (mostRecentViewedPostId) {
        updateLastSeenPostMutation({
          variables: {
            input: {
              threadId,
              postId: mostRecentViewedPostId,
            },
          },
        }).catch((error) => {
          console.error("Failed to update last seen post on thread exit:", error);
        });
      }
    };
  }, [mostRecentViewedPostId, threadId, updateLastSeenPostMutation]);

  const handleResponse = async (message: string) => {
    if (!currentUser || !message.trim()) {
      throw new Error("User not authenticated or empty message");
    }

    try {
      const result = await createPostMutation({
        variables: {
          input: {
            message: message.trim(),
            threadId,
            replyToPostId: initialPost?.id,
          },
        },
      });

      if (!result.data?.createPost?.success) {
        throw new Error(result.data?.createPost?.message || "Failed to create response");
      }

      await refetch();
    } catch (error) {
      console.error("Error creating response:", error);
      throw error;
    }
  };

  const handleReply = async (postId: string, message: string) => {
    if (!currentUser || !message.trim()) {
      throw new Error("User not authenticated or empty message");
    }

    try {
      const result = await createPostMutation({
        variables: {
          input: {
            message: message.trim(),
            threadId,
            replyToPostId: postId,
          },
        },
      });

      if (!result.data?.createPost?.success) {
        throw new Error(result.data?.createPost?.message || "Failed to create reply");
      }

      await refetch();
    } catch (error) {
      console.error("Error creating reply:", error);
      throw error;
    }
  };

  if (!currentUser) {
    return (
      <View style={styles.container}>
        <DiscussionHeader
          title={threadTitle}
          author="Unknown"
          numberOfComments={0}
          message="User not authenticated"
          handleResponse={() => {}}
          currentUser={null}
        />
        <ResponseCardList
          posts={[]}
          onReply={() => {}}
          onPostViewed={() => {}}
          currentUser={{ id: "", name: "" }}
          loading={false}
          error="User not authenticated"
        />
      </View>
    );
  }

  // Calculate response count for header
  const responseCount = responsePosts.length;

  // Get discussion details from initial post
  const discussionAuthor = initialPost?.author?.name || "Unknown Author";
  const discussionMessage = initialPost?.message || "";

  return (
    <View style={styles.container}>
      {/* Discussion Header with initial post content */}
      <DiscussionHeader
        title={threadTitle}
        author={discussionAuthor}
        numberOfComments={responseCount}
        message={discussionMessage}
        handleResponse={handleResponse}
        currentUser={currentUser}
      />

      {/* Response Cards List */}
      <ResponseCardList
        posts={responsePosts}
        onReply={handleReply}
        onPostViewed={handlePostViewed}
        currentUser={currentUser}
        loading={loading}
        error={error?.message || ""}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default StudentDiscussionContainer;
